import { useEffect, useRef } from "react";
import type { Guidance, Speaker, Turn } from "../types";

interface Options {
  enabled: boolean;
  guidance: Guidance | null;
  transcript: Turn[];
  send: (speaker: Speaker, text: string) => void;
  onSent?: (text: string) => void;
}

/**
 * Autopilot: send the Copilot's suggested response to the customer automatically.
 *
 * This deliberately inverts the product's central constraint. Everywhere else the
 * Copilot is an observer - it drafts, and a human decides whether those words are good
 * enough to send. With autopilot on there is no human in the loop, so the safeguards
 * that matter are the ones that decide when NOT to fire:
 *
 *   - `degraded` guidance is never sent. A node failed and the draft is a fallback, not
 *     a considered answer; that is exactly when a human should be reading it.
 *   - Only fires when the customer spoke last. Without this, the stale guidance replayed
 *     on the `state` frame after a reconnect would send a duplicate reply.
 *   - Fires at most once per guidance payload. An agent turn short-circuits the graph, so
 *     the backend re-emits the previous (unchanged) guidance; keying on turn index plus
 *     timestamp stops that echo from becoming a send loop.
 *
 * The toggle defaults to off and is persisted per tab, never per user - leaving it on by
 * accident should not outlive the browser session.
 */
export function useAutopilot({ enabled, guidance, transcript, send, onSent }: Options) {
  const lastSentKey = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !guidance) return;

    const suggestion = guidance.suggested_response?.text?.trim();
    if (!suggestion) return;

    // A failed node produced this. Hand it back to a human rather than sending it.
    if (guidance.degraded) return;

    // Only reply when the customer is the one waiting.
    if (transcript[transcript.length - 1]?.speaker !== "customer") return;

    const key = `${guidance.turn_index}:${guidance.generated_at}`;
    if (lastSentKey.current === key) return;
    lastSentKey.current = key;

    send("agent", suggestion);
    onSent?.(suggestion);
  }, [enabled, guidance, transcript, send, onSent]);

  // Re-arm on toggle off, so switching back on replies to the turn currently waiting
  // rather than silently skipping it as already handled.
  useEffect(() => {
    if (!enabled) lastSentKey.current = null;
  }, [enabled]);
}
